import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  EnhancedSeoHead,
  generateProviderDetailSchemas,
  NotFoundPage,
  ProviderDetailPage,
  providerDetailUrl,
  useResponseStatus,
} from "@appcaire/shared/seo";
import {
  useGetConsumerProviderWithDetailsQuery,
  useGetMunicipalityQuery,
} from "@appcaire/graphql";
import { Loader2 } from "lucide-react";

const CANONICAL_ORIGIN = "https://nackahemtjanst.se";
const NACKA_SLUG = "nacka";
const NACKA_MUNICIPALITY_DISPLAY_NAME = "Nacka kommun";

const ProviderUnavailableState = () => {
  useResponseStatus(503);
  return (
    <div className="mx-auto min-h-[60vh] max-w-2xl px-4 py-20" role="alert">
      <h1 className="text-3xl font-bold text-surface-900">
        Utförarinformationen är tillfälligt otillgänglig
      </h1>
    </div>
  );
};

/**
 * Local Nacka provider detail route. Renders the shared consumer profile;
 * canonical URL stays on nackahemtjanst.se so the local provider URLs listed
 * in the Nacka sitemap are indexable, municipality-scoped documents.
 *
 * Nacka is a municipality-scoped directory. Provider links always resolve the
 * exact authoritative Nacka presence; arbitrary query-string scope is ignored.
 */
const ProviderPage: React.FC = () => {
  const { providerSlug } = useParams<{ providerSlug: string }>();
  const navigate = useNavigate();

  const {
    data: providerData,
    loading: providerLoading,
    error: providerError,
  } = useGetConsumerProviderWithDetailsQuery({
    variables: {
      slug: providerSlug || "",
      municipalitySlug: NACKA_SLUG,
    },
    skip: !providerSlug,
  });

  const provider = providerData?.consumerProviderWithDetails ?? null;

  const {
    data: municipalityData,
    loading: municipalityLoading,
    error: municipalityError,
  } = useGetMunicipalityQuery({
    variables: { slug: NACKA_SLUG },
  });

  const municipality = municipalityData?.municipality ?? null;
  const canonicalUrl = providerSlug
    ? `${CANONICAL_ORIGIN}${providerDetailUrl(providerSlug, NACKA_SLUG)}`
    : `${CANONICAL_ORIGIN}/anordnare`;

  if (providerError || municipalityError) {
    return <ProviderUnavailableState />;
  }

  if (providerLoading || municipalityLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center bg-surface-50">
        <Loader2 className="mx-auto h-10 w-10 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!provider) {
    return (
      <NotFoundPage
        title="Anordnare hittades inte"
        description="Vi kunde inte hitta den här utföraren."
        navigationLinks={[
          { label: "Tillbaka till anordnare", to: "/utforare" },
          { label: "Hitta hemtjänst", to: "/hitta-hemtjanst" },
          { label: "Startsidan", to: "/" },
        ]}
      />
    );
  }

  const municipalityName =
    municipality?.name || provider.presence?.municipality?.name || "Nacka";
  const description = `Detaljerad information om ${provider.name} — hemtjänstutförare i ${municipalityName}.`;
  const providerSchemas = generateProviderDetailSchemas({
    canonicalUrl,
    description,
    provider,
    municipalityName,
    municipalities: [municipalityName],
    breadcrumbs: [
      { name: "Hem", url: `${CANONICAL_ORIGIN}/` },
      // Every crumb must be a route this site serves: the provider list is
      // `/utforare`; `/hitta-hemtjanst/nacka` never existed here and 404'd.
      { name: "Anordnare", url: `${CANONICAL_ORIGIN}/utforare` },
      { name: provider.name, url: canonicalUrl },
    ],
  });

  return (
    <>
      <EnhancedSeoHead
        title={`${provider.name} | Hemtjänst i ${municipalityName}`}
        description={description}
        canonicalUrl={canonicalUrl}
        ogType="profile"
        contentClassification="Business"
        targetAudience="seniors, families"
        schemaMarkup={providerSchemas}
      />
      <ProviderDetailPage
        provider={provider}
        variant="consumer"
        municipalityName={municipalityName}
        municipalitySlug={NACKA_SLUG}
        municipalityRankScope={{ slug: NACKA_SLUG }}
        municipalityRankDisplayName={NACKA_MUNICIPALITY_DISPLAY_NAME}
        officialChooserUrl={municipality?.providerListUrl ?? null}
        onBack={() => navigate("/utforare")}
        backLabel="Tillbaka till anordnare"
        contactFormEnabled
        contactSource="nackahemtjanst-provider"
        consumerConfig={{
          officialSourceName: municipalityName,
          showCaireIndex: true,
          caireIndexMethodologyHref:
            "https://sverigeshemtjanst.se/topplistor/metodik",
        }}
      />
    </>
  );
};

export default ProviderPage;
